import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Tag } from "@/components/Reveal";
import { ParallaxImage } from "@/components/ParallaxImage";
import { Counter } from "@/components/Counter";
import { IMG } from "@/data/images";

const CHAIN = [
  { n: "01", stage: "Harvest", note: "Rough handling and field heat start the clock on decay within hours." },
  { n: "02", stage: "Transport", note: "Long distances and temperature swings accelerate respiration and moisture loss." },
  { n: "03", stage: "Storage", note: "Cold-chain gaps — even brief ones — can cut shelf life dramatically." },
  { n: "04", stage: "Retail shelf", note: "Produce that looks tired gets discounted, then discarded." },
  { n: "05", stage: "Kitchen", note: "In homes and professional kitchens, wilted produce goes straight to waste." },
];

export default function TheProblem() {
  return (
    <div data-testid="the-problem-page">
      <PageHero
        chapter="02"
        tag="The Problem"
        testId="problem-hero"
        lines={[<>A third of the world&rsquo;s food</>, <>never reaches</>, <>a plate.</>]}
        lead="Food loss is not a farming problem or a consumer problem — it is a chain of small failures that begins the moment produce is picked."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24">
        <Reveal>
          <div className="bg-deep text-cream rounded-3xl p-10 sm:p-16" data-testid="problem-data-wall">
            <Tag light>The data wall</Tag>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
              <Counter dark value={33} suffix="%" label="of all food produced is lost or wasted before it is eaten" sub="FAO estimate" testId="problem-counter-global" />
              <Counter dark value={40} prefix="SAR " suffix="B+" label="estimated annual value of food loss and waste in Saudi Arabia" sub="National studies" testId="problem-counter-ksa" />
              <Counter dark value={40} suffix="%+" label="reported loss and waste rate for vegetables in Saudi Arabia" sub="Published national estimates" testId="problem-counter-postharvest" />
              <Counter dark value={10} suffix="%" label="of global greenhouse-gas emissions are linked to food loss and waste" sub="UNEP estimate" testId="problem-counter-ghg" />
            </div>
            <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.2em] text-sage/70">
              Figures are published estimates; ranges vary by source and methodology.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24 sm:pb-32">
        <Reveal><Tag>Where freshness is lost</Tag></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display font-extrabold tracking-tight leading-[1.1] text-3xl sm:text-4xl text-forest max-w-2xl">
            Five weak points. One compounding problem.
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {CHAIN.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div
                data-testid={`chain-card-${c.n}`}
                className="border border-forest/10 bg-white p-6 h-full transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-forest/10"
              >
                <span className="font-mono text-xs text-amber">{c.n}</span>
                <h3 className="mt-4 font-display font-bold text-lg text-forest">{c.stage}</h3>
                <p className="mt-3 text-sm leading-relaxed text-moss">{c.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-forest/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <Reveal><Tag>The true cost</Tag></Reveal>
            <Reveal delay={0.1}>
              <blockquote className="mt-8 font-serif italic text-3xl sm:text-4xl leading-snug text-forest">
                &ldquo;Every tonne of produce wasted is water, land, energy and labour —
                spent for nothing.&rdquo;
              </blockquote>
            </Reveal>
            <Reveal delay={0.2} className="mt-8 text-base sm:text-lg leading-relaxed text-moss space-y-4">
              <p>
                In arid regions the stakes multiply. Food that is imported or grown with scarce
                water carries an enormous hidden footprint — and when it spoils, that footprint
                is paid for twice: once to produce, once to dispose.
              </p>
              <p>
                Extending shelf life is one of the highest-leverage interventions in the chain:
                it protects value that already exists instead of demanding more resources.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <ParallaxImage src={IMG.wheatField} alt="Golden agricultural field at sunset" ratio="aspect-[4/3]" speed={9} testId="problem-image-field" />
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 text-center">
        <Reveal>
          <h2 className="font-display font-extrabold tracking-tight leading-[1.1] text-3xl sm:text-4xl text-forest max-w-2xl mx-auto">
            The problem is enormous. The fix starts at the surface of the food itself.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <Link
            to="/our-solution"
            data-testid="problem-cta-solution"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-forest text-cream px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-emerald"
          >
            See our solution
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
